import type {
    TaskTemplateDependencyAdd,
    TaskTemplateDependencyRemove,
    TaskTemplateEdgeRef,
    TaskTemplateRelationAdd,
    TaskTemplateRelationRemove,
} from "@common/schemas/task-template.schema";
import { normalizeRelationIds } from "@common/helpers/taskRelation";
import type { TaskTemplate } from "@common/types/taskTemplate";
import { isTaskSavable } from "./closeTemplateTaskLinks";
import type { LocalTask } from "./closeTemplateTasksDrawer.types";

export interface TaskEdgeDiff {
    dependencies: {
        adds: TaskTemplateDependencyAdd[];
        removes: TaskTemplateDependencyRemove[];
    };
    relations: {
        adds: TaskTemplateRelationAdd[];
        removes: TaskTemplateRelationRemove[];
    };
}

type EdgePair = readonly [string, string];

const pairKey = ([first, second]: EdgePair): string =>
    JSON.stringify([first, second]);

const toRef = (task: LocalTask): TaskTemplateEdgeRef =>
    task.persistedId
        ? { kind: "persisted", id: task.persistedId }
        : { kind: "local", localId: task.localId };

const uniquePairs = (pairs: EdgePair[]): Map<string, EdgePair> =>
    new Map(pairs.map((pair) => [pairKey(pair), pair]));

const subtract = (
    from: Map<string, EdgePair>,
    other: Map<string, EdgePair>,
): EdgePair[] => [...from].filter(([key]) => !other.has(key)).map(([, v]) => v);

export function computeTaskEdgeDiff(
    fetchedTasks: TaskTemplate[] | undefined,
    localTasks: LocalTask[],
): TaskEdgeDiff {
    const eligibleTasks = localTasks.filter(isTaskSavable);
    const eligibleByLocalId = new Map(
        eligibleTasks.map((task) => [task.localId, task]),
    );
    const localIdByPersistedId = new Map(
        eligibleTasks.flatMap((task) =>
            task.persistedId ? [[task.persistedId, task.localId] as const] : [],
        ),
    );

    const localDependencies: EdgePair[] = [];
    const localRelations: EdgePair[] = [];
    for (const task of eligibleTasks) {
        for (const blockerId of task.blockedByLocalIds) {
            if (
                blockerId === task.localId ||
                !eligibleByLocalId.has(blockerId)
            ) {
                continue;
            }
            localDependencies.push([blockerId, task.localId]);
        }
        for (const relatedId of task.relatedLocalIds) {
            if (
                relatedId === task.localId ||
                !eligibleByLocalId.has(relatedId)
            ) {
                continue;
            }
            localRelations.push(normalizeRelationIds(task.localId, relatedId));
        }
    }

    const originalDependencies: EdgePair[] = [];
    const originalRelations: EdgePair[] = [];
    for (const task of fetchedTasks ?? []) {
        const taskLocalId = localIdByPersistedId.get(task.id);
        if (taskLocalId === undefined) {
            continue;
        }
        for (const blockerPersistedId of task.blockedByTaskTemplateIds) {
            const blockerLocalId = localIdByPersistedId.get(blockerPersistedId);
            if (blockerLocalId === undefined) {
                continue;
            }
            originalDependencies.push([blockerLocalId, taskLocalId]);
        }
        for (const relatedPersistedId of task.relatedTaskTemplateIds) {
            const relatedLocalId = localIdByPersistedId.get(relatedPersistedId);
            if (relatedLocalId === undefined) {
                continue;
            }
            originalRelations.push(
                normalizeRelationIds(taskLocalId, relatedLocalId),
            );
        }
    }

    const localDependencyMap = uniquePairs(localDependencies);
    const originalDependencyMap = uniquePairs(originalDependencies);
    const localRelationMap = uniquePairs(localRelations);
    const originalRelationMap = uniquePairs(originalRelations);

    const refOf = (localId: string): TaskTemplateEdgeRef | undefined => {
        const task = eligibleByLocalId.get(localId);
        return task ? toRef(task) : undefined;
    };
    const persistedIdOf = (localId: string): string | undefined =>
        eligibleByLocalId.get(localId)?.persistedId;

    const dependencyAdds = subtract(localDependencyMap, originalDependencyMap);
    const dependencyRemoves = subtract(
        originalDependencyMap,
        localDependencyMap,
    );
    const relationAdds = subtract(localRelationMap, originalRelationMap);
    const relationRemoves = subtract(originalRelationMap, localRelationMap);

    return {
        dependencies: {
            adds: dependencyAdds.flatMap(([blockingId, blockedId]) => {
                const blocking = refOf(blockingId);
                const blocked = refOf(blockedId);
                return blocking && blocked ? [{ blocking, blocked }] : [];
            }),
            removes: dependencyRemoves.flatMap(([blockingId, blockedId]) => {
                const blockingPersistedId = persistedIdOf(blockingId);
                const blockedPersistedId = persistedIdOf(blockedId);
                return blockingPersistedId && blockedPersistedId
                    ? [
                          {
                              blockingId: blockingPersistedId,
                              blockedId: blockedPersistedId,
                          },
                      ]
                    : [];
            }),
        },
        relations: {
            adds: relationAdds.flatMap(([firstId, secondId]) => {
                const a = refOf(firstId);
                const b = refOf(secondId);
                return a && b ? [{ a, b }] : [];
            }),
            removes: relationRemoves.flatMap(([firstId, secondId]) => {
                const aId = persistedIdOf(firstId);
                const bId = persistedIdOf(secondId);
                return aId && bId ? [{ aId, bId }] : [];
            }),
        },
    };
}

const hasGroupChanges = ({
    adds,
    removes,
}: {
    adds: readonly object[];
    removes: readonly object[];
}) => adds.length > 0 || removes.length > 0;

export const hasDependencyChanges = ({ dependencies }: TaskEdgeDiff) =>
    hasGroupChanges(dependencies);

export const hasRelationChanges = ({ relations }: TaskEdgeDiff) =>
    hasGroupChanges(relations);

export const hasEdgeChanges = (diff: TaskEdgeDiff) =>
    hasDependencyChanges(diff) || hasRelationChanges(diff);
